import { EncodingOperation, EncoderToolType } from '../types';

export interface UrlEncoderConfiguration {
  id: string;
  operation: 'encode' | 'decode';
  input: string;
  caseHandling?: 'preserve' | 'uppercase' | 'lowercase';
  spaceHandling?: 'preserve' | 'plus' | 'percent20' | 'encode';
  partialEncoding?: boolean;
  encodedAt: Date;
}

export class UrlEncoderService {
  private static readonly DEFAULT_CONFIG: Partial<UrlEncoderConfiguration> = {
    caseHandling: 'preserve',
    spaceHandling: 'percent20',
    partialEncoding: false
  };

  static createConfiguration(
    input: string,
    operation: 'encode' | 'decode' = 'encode',
    options: Partial<UrlEncoderConfiguration> = {}
  ): UrlEncoderConfiguration {
    return {
      id: `url-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      operation,
      input,
      ...this.DEFAULT_CONFIG,
      ...options,
      encodedAt: new Date()
    };
  }

  static async encode(config: UrlEncoderConfiguration): Promise<EncodingOperation> {
    try {
      let result = config.input;

      if (config.partialEncoding) {
        // Encode only unsafe characters, leaving safe characters unencoded
        result = this.partialEncode(result, config.spaceHandling, config.caseHandling);
      } else {
        // Full URL encoding
        if (config.spaceHandling === 'plus') {
          result = encodeURIComponent(result)
            .replace(/%20/g, '+')
            .replace(/!/g, '%21')
            .replace(/\*/g, '%2A')
            .replace(/\(/g, '%28')
            .replace(/\)/g, '%29');
        } else if (config.spaceHandling === 'percent20' || config.spaceHandling === 'encode') {
          result = encodeURIComponent(result)
            .replace(/!/g, '%21')
            .replace(/\*/g, '%2A')
            .replace(/\(/g, '%28')
            .replace(/\)/g, '%29');
        } else {
          result = this.preserveSpaces(
            encodeURIComponent(result)
              .replace(/!/g, '%21')
              .replace(/\*/g, '%2A')
              .replace(/\(/g, '%28')
              .replace(/\)/g, '%29')
          );
        }
      }

      // Apply case handling
      result = this.applyCaseHandling(result, config.caseHandling);

      return {
        id: config.id,
        toolType: EncoderToolType.URL_ENCODER,
        operationType: 'encode',
        inputData: config.input,
        outputData: result,
        timestamp: config.encodedAt,
        status: 'success',
        metadata: {
          caseHandling: config.caseHandling,
          spaceHandling: config.spaceHandling,
          partialEncoding: config.partialEncoding
        }
      };
    } catch (error) {
      return {
        id: config.id,
        toolType: EncoderToolType.URL_ENCODER,
        operationType: 'encode',
        inputData: config.input,
        timestamp: config.encodedAt,
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Encoding failed'
      };
    }
  }

  static async decode(config: UrlEncoderConfiguration): Promise<EncodingOperation> {
    try {
      let result = config.input;

      // Always replace + with space in decoding (standard URL encoding behavior)
      result = result.replace(/\+/g, ' ');

      // Decode the URL
      result = decodeURIComponent(result);

      // Apply case handling
      result = this.applyCaseHandling(result, config.caseHandling);

      return {
        id: config.id,
        toolType: EncoderToolType.URL_ENCODER,
        operationType: 'decode',
        inputData: config.input,
        outputData: result,
        timestamp: config.encodedAt,
        status: 'success',
        metadata: {
          caseHandling: config.caseHandling,
          spaceHandling: config.spaceHandling,
          partialEncoding: config.partialEncoding
        }
      };
    } catch (error) {
      return {
        id: config.id,
        toolType: EncoderToolType.URL_ENCODER,
        operationType: 'decode',
        inputData: config.input,
        timestamp: config.encodedAt,
        status: 'error',
        errorMessage: error instanceof Error ? `Decoding failed: ${error.message}` : 'Decoding failed'
      };
    }
  }

  static async batchEncode(inputs: string[], options: Partial<UrlEncoderConfiguration> = {}): Promise<EncodingOperation[]> {
    const operations: EncodingOperation[] = [];
    
    for (const input of inputs) {
      const config = this.createConfiguration(input, 'encode', options);
      const operation = await this.encode(config);
      operations.push(operation);
    }

    return operations;
  }

  static async batchDecode(inputs: string[], options: Partial<UrlEncoderConfiguration> = {}): Promise<EncodingOperation[]> {
    const operations: EncodingOperation[] = [];
    
    for (const input of inputs) {
      const config = this.createConfiguration(input, 'decode', options);
      const operation = await this.decode(config);
      operations.push(operation);
    }

    return operations;
  }

  static validateUrl(url: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!url || url.trim().length === 0) {
      errors.push('URL cannot be empty');
      return { isValid: false, errors };
    }

    try {
      // Basic URL structure validation - decode to verify valid encoding
      decodeURIComponent(url);

      // Check for invalid URL patterns
      if (url.includes('..')) {
        errors.push('URL contains potentially dangerous path traversal');
      }

      if (url.includes('\0')) {
        errors.push('URL contains null bytes');
      }

      // Check for excessively long URLs
      if (url.length > 2048) {
        errors.push('URL exceeds maximum length');
      }

    } catch (error) {
      errors.push('URL contains invalid encoding');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  
  private static partialEncode(
    input: string,
    spaceHandling: 'preserve' | 'plus' | 'percent20' | 'encode' | undefined,
    caseHandling: 'preserve' | 'uppercase' | 'lowercase' | undefined
  ): string {
    // Characters that are always safe in URLs (unreserved characters per RFC 3986)
    const safeChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_~';
    let result = '';

    for (const char of input) {
      if (safeChars.includes(char) || (char === ' ' && spaceHandling === 'preserve')) {
        result += char;
      } else if (char === ' ' && spaceHandling === 'plus') {
        result += '+';
      } else if (char === ' ' && (spaceHandling === 'percent20' || spaceHandling === 'encode')) {
        result += '%20';
      } else {
        // Manually encode special characters that encodeURIComponent doesn't handle
        const encoded = encodeURIComponent(char);
        const manuallyEncoded = encoded
          .replace(/!/g, '%21')
          .replace(/\*/g, '%2A')
          .replace(/\(/g, '%28')
          .replace(/\)/g, '%29')
          .replace(/'/g, '%27');
        result += manuallyEncoded;
      }
    }

    return this.applyCaseHandling(result, caseHandling);
  }

  private static preserveSpaces(encoded: string): string {
    return encoded.replace(/%20/g, ' ');
  }

  private static applyCaseHandling(
    input: string,
    caseHandling: 'preserve' | 'uppercase' | 'lowercase' | undefined
  ): string {
    switch (caseHandling) {
      case 'uppercase':
        return input.toUpperCase();
      case 'lowercase':
        return input.toLowerCase();
      case 'preserve':
      default:
        return input;
    }
  }

  /**
   * Clears any cached data (for testing)
   */
  static clearCache(): void {
    // No-op: This service doesn't maintain cache
  }
}